import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSystemUsageTracker } from '../hooks/useSystemUsageTracker';
import { useAuth } from '../hooks/useAuth';
import { safeInvoke } from '../lib/safeInvoke';
import { safeParseJSON } from '../lib/sanitizeDisplay';
import { PhaseLoader } from '../components/shared/PhaseLoader';
import { ReadingFeedback } from '../components/shared/ReadingFeedback';
import { ReadingActions } from '../components/shared/ReadingActions';
import { EmptyState } from '../components/ui/EmptyState';
import { useI18n } from '../hooks/useI18n';

interface VedicResult {
  moonSign: string;
  sunSign: string;
  nakshatra: string;
  yogas: string[];
  dashaCurrentPeriod: string;
  sections: {
    mapaVedico?: string;
    nakshatra?: string;
    dashas?: string;
    yogas?: string;
    sinteseGeral?: string;
  };
  readingId: string | null;
  text: string;
}

const TABS = [
  { key: 'sinteseGeral', label: '✨ Síntese' },
  { key: 'mapaVedico',   label: '🌟 Mapa Védico' },
  { key: 'nakshatra',    label: '⭐ Nakshatra' },
  { key: 'dashas',       label: '📅 Dashas' },
  { key: 'yogas',        label: '🕉️ Yogas' },
];

const LOADING_PHASES = [
  'Calculando posições no zodíaco védico...',
  'Analisando as casas Bhava do seu mapa...',
  'Consultando as 27 Nakshatras...',
  'Preparando sua leitura Jyotish personalizada...',
];

export function VedicAstrologyPage() {
  useSystemUsageTracker('astrologia_vedica');
  const { profile } = useAuth();
  const navigate    = useNavigate();
  const { t }       = useI18n();

  const [loading,   setLoading]   = useState(false);
  const [result,    setResult]    = useState<VedicResult | null>(null);
  const [error,     setError]     = useState<string | null>(null);
  const [started,   setStarted]   = useState(false);

  async function generate() {
    setStarted(true);
    setLoading(true);
    setError(null);

    const { data, fromFallback } = await safeInvoke<VedicResult>('vedic-astrology', {
      body:     {},
      fallback: { moonSign: '', sunSign: '', nakshatra: '', yogas: [], dashaCurrentPeriod: '', sections: {}, readingId: null, text: '' },
      timeout:  30_000,
    });

    setLoading(false);
    if (fromFallback || !data.text) {
      setError(t('pages.vedicAstrology.error'));
      return;
    }
    // Client-side JSON section parsing fallback
    let sections = data.sections;
    let text = data.text;
    if (!sections?.sinteseGeral) {
      const parsed = safeParseJSON<{ sinteseGeral?: string; mapaVedico?: string; nakshatra?: string; dashas?: string; yogas?: string }>(text);
      if (parsed?.sinteseGeral) {
        sections = { sinteseGeral: parsed.sinteseGeral, mapaVedico: parsed.mapaVedico ?? '', nakshatra: parsed.nakshatra ?? '', dashas: parsed.dashas ?? '', yogas: parsed.yogas ?? '' };
        text = parsed.sinteseGeral;
      } else if (text.trimStart().startsWith('{')) {
        text = 'Astrologia Védica temporariamente indisponível ✦';
      }
    }
    setResult({ ...data, sections, text });
  }

  if (!profile?.birth_date) {
    return (
      <div className="page-container">
        <EmptyState emoji="🕉️" title={t('pages.vedicAstrology.emptyTitle')}
          subtitle={t('pages.vedicAstrology.emptySubtitle')}
          cta={t('pages.vedicAstrology.emptyCta')} ctaAction={() => navigate('/profile')} />
      </div>
    );
  }

  return (
    <div className="page-container">
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 className="font-display" style={{ fontSize: 'clamp(1.375rem,4vw,1.875rem)', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
          {t('pages.vedicAstrology.title')}
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
          {t('pages.vedicAstrology.subtitle')}
        </p>
      </div>

      {!started && !result && (
        <div className="card" style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
          <span style={{ fontSize: '2.5rem', display: 'block', marginBottom: '1rem' }}>🕉️</span>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '0.75rem', lineHeight: 1.6 }}>
            {t('pages.vedicAstrology.intro')}
          </p>
          {/* Vedic vs Western explanation */}
          <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-card)', padding: '0.75rem 1rem', marginBottom: '1rem', textAlign: 'left' }}>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', lineHeight: 1.6, margin: 0 }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Védica vs Ocidental:</strong> A astrologia védica (Jyotish) usa o zodíaco sideral, alinhado às constelações reais, e costuma diferir 23° do zodíaco tropical ocidental. Por isso, seu Sol ou Lua podem aparecer em signos diferentes aqui. Ambas as tradições são válidas e complementares.
            </p>
          </div>
          {!profile.birth_time && (
            <p style={{ color: 'var(--petrol)', fontSize: 'var(--text-caption)', marginBottom: '1rem', lineHeight: 1.5 }}>
              {t('pages.vedicAstrology.addBirthTime')}
              <button className="btn btn-ghost btn-sm" onClick={() => navigate('/profile')} style={{ display: 'inline', marginLeft: '0.25rem', padding: '0 0.25rem' }}>
                →
              </button>
            </p>
          )}
          <button className="btn btn-primary" onClick={generate}>
            {t('pages.vedicAstrology.consultCta')}
          </button>
        </div>
      )}

      {loading && (
        <div className="card">
          <PhaseLoader phases={LOADING_PHASES} />
        </div>
      )}

      {error && !loading && (
        <div style={{ padding: '1rem', backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 'var(--radius-md)', color: '#fca5a5', fontSize: 'var(--text-label)', marginBottom: '1.5rem' }}>
          {error}
          <br />
          <button className="btn btn-secondary btn-sm" style={{ marginTop: '0.75rem' }} onClick={generate}>
            Tentar novamente
          </button>
        </div>
      )}

      {result && !loading && (
        <div className="animate-fade-in">
          {/* Key placements */}
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>
              {t('pages.vedicAstrology.keyPositions')}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {[
                { label: t('pages.vedicAstrology.moonLabel'), value: result.moonSign },
                { label: t('pages.vedicAstrology.sunLabel'), value: result.sunSign },
                { label: t('pages.vedicAstrology.nakshatraLabel'), value: result.nakshatra },
              ].map(item => (
                <div key={item.label} style={{ backgroundColor: 'var(--bg-base)', borderRadius: 'var(--radius-md)', padding: '0.75rem' }}>
                  <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: '0 0 0.25rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{item.label}</p>
                  <p style={{ fontSize: 'var(--text-label)', color: 'var(--text-primary)', fontWeight: 600, margin: 0 }}>{item.value || '-'}</p>
                </div>
              ))}
            </div>

            {result.dashaCurrentPeriod && (
              <div style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: 'rgba(31,78,95,0.06)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-gold)' }}>
                <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', margin: '0 0 0.25rem', fontWeight: 600 }}>{t('pages.vedicAstrology.dashaLabel')}</p>
                <p style={{ fontSize: 'var(--text-label)', color: 'var(--text-primary)', fontWeight: 600, margin: 0 }}>{result.dashaCurrentPeriod}</p>
              </div>
            )}

            {result.yogas.length > 0 && (
              <div style={{ marginTop: '0.75rem' }}>
                <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: '0 0 0.5rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('pages.vedicAstrology.yogasLabel')}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  {result.yogas.map((y, i) => (
                    <span key={i} style={{ padding: '0.2rem 0.6rem', backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-gold)', borderRadius: 'var(--radius-full)', fontSize: 'var(--text-caption)', color: 'var(--petrol)' }}>
                      {y}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Stacked sections */}
          {TABS.map(tab => {
            const sections = result.sections as Record<string, string>;
            const text = sections[tab.key] || (tab.key === 'sinteseGeral' ? result.text : '');
            if (!text) return null;
            return (
              <div key={tab.key} className="card" style={{ marginBottom: '1.5rem' }}>
                <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem', fontSize: 'var(--text-label)' }}>
                  {tab.label}
                </p>
                <div className="reading-text">
                  {text.split('\n').filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </div>
            );
          })}

          <ReadingActions pageName="Astrologia Védica" reading={result.text} signEmoji="🕉️" />
          <ReadingFeedback feature="vedic-astrology" readingId={result.readingId ?? undefined} />

          <button className="btn btn-secondary btn-sm" style={{ marginTop: '1rem' }} onClick={() => { setResult(null); setStarted(false); }}>
            {t('pages.vedicAstrology.newConsult')}
          </button>
        </div>
      )}
    </div>
  );
}
